// Tablero de Feedback sobre TanStack Query. La cache de la query es la fuente
// de verdad del listado; las mutaciones invalidan esa key en vez de parchear
// la lista a mano.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarComentarioRequest,
  actualizarEstadoRequest,
  crearComentarioRequest,
  eliminarComentarioRequest,
  fetchAutoresDeFeedback,
  fetchCatalogosDeFeedback,
  fetchFeedback,
  type FiltrosDeFeedback,
  type NuevoComentario,
} from "@/lib/feedback";

export const FEEDBACK_KEY = ["feedback"] as const;

/**
 * Keys HERMANAS, no hijas (`["feedback", "catalogos"]`): TanStack Query
 * invalida por PREFIJO, así que colgarlas del tablero haría que cada
 * comentario nuevo volviera a pedir también las listas del formulario.
 *
 * Y separadas entre sí porque cambian a ritmos distintos: los catálogos son
 * enums (cambian al desplegar), los autores crecen cada vez que alguien
 * comenta por primera vez.
 */
export const FEEDBACK_CATALOGOS_KEY = ["feedback-catalogos"] as const;
export const FEEDBACK_AUTORES_KEY = ["feedback-autores"] as const;

/**
 * El tablero.
 *
 * Los filtros y la página van DENTRO de la queryKey: cada combinación es una
 * respuesta distinta del servidor —filtra, ordena y pagina él— y con una key
 * única la cache las guarda por separado.
 *
 * `placeholderData` conserva el resultado anterior mientras llega el nuevo:
 * sin él, cambiar de filtro o de página vacía la lista y la pantalla salta a
 * los esqueletos en cada clic.
 */
export function useFeedback(filtros: FiltrosDeFeedback = {}) {
  return useQuery({
    queryKey: [...FEEDBACK_KEY, filtros],
    queryFn: () => fetchFeedback(filtros),
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Las listas del formulario (módulos, tipos, estados).
 *
 * `staleTime` alto: son enums, cambian cuando se despliega una versión nueva
 * del sistema, no mientras alguien escribe. Sin él, cada vez que la ventana
 * recupera el foco se dispara un refetch que puede reordenar el select bajo
 * el cursor.
 */
export function useCatalogosDeFeedback() {
  return useQuery({
    queryKey: FEEDBACK_CATALOGOS_KEY,
    queryFn: fetchCatalogosDeFeedback,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Las opciones del filtro por autor.
 *
 * `staleTime` mucho más corto que el de los catálogos: esta lista SÍ cambia
 * durante la sesión —alguien comenta por primera vez y pasa a ser filtrable—.
 */
export function useAutoresDeFeedback() {
  return useQuery({
    queryKey: FEEDBACK_AUTORES_KEY,
    queryFn: fetchAutoresDeFeedback,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Deja un comentario.
 *
 * Invalida el tablero ENTERO (todas las combinaciones de filtro y página) y
 * no solo la primera: el comentario nuevo entra arriba y empuja a los demás,
 * así que cualquier página cacheada quedó corrida en uno.
 *
 * Y también los AUTORES: si es la primera vez que esta persona comenta, acaba
 * de aparecer en el filtro. Los catálogos no se tocan — son enums.
 *
 * Invalida en vez de sembrar la respuesta en la lista: el orden, el filtro y
 * la paginación los decide el servidor, y meter la fila a mano en la página 1
 * la dejaría con 26 elementos donde el backend devuelve 25 (o dentro de un
 * filtro que no le corresponde).
 */
export function useCrearComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: crearComentarioRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
      queryClient.invalidateQueries({ queryKey: FEEDBACK_AUTORES_KEY });
    },
  });
}

/**
 * Corrige el contenido de un comentario.
 *
 * Invalida el tablero y no siembra la fila devuelta: la edición puede cambiar
 * el MÓDULO o el TIPO, o sea sacar el comentario del filtro que está activo.
 * Parchear la fila en su lugar la dejaría visible bajo un filtro que ya no
 * cumple.
 */
export function useActualizarComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: NuevoComentario & { id: number }) =>
      actualizarComentarioRequest(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY }),
  });
}

interface OpcionesDeBorrado {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Borra un comentario.
 *
 * Los callbacks van a nivel `useMutation` y no en `mutate()` por el mismo
 * motivo que en `useEliminarItem`: los de `mutate()` se descartan si el
 * diálogo se desmonta antes de que la mutación termine, y ahí el rechazo del
 * backend no produciría ninguna señal.
 *
 * Invalida también los AUTORES: si era el único comentario de esa persona,
 * dejó de ser filtrable y el select ofrecería un filtro que no devuelve nada.
 */
export function useEliminarComentario(opciones: OpcionesDeBorrado = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: eliminarComentarioRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
      queryClient.invalidateQueries({ queryKey: FEEDBACK_AUTORES_KEY });
      opciones.onSuccess?.();
    },
    onError: (error) => opciones.onError?.(error),
  });
}

/**
 * Mueve el estado de un comentario (solo Super Admin).
 *
 * Invalida el tablero completo igual que las otras: el estado no reordena
 * nada hoy, pero es el mismo listado y sembrar a mano una fila dentro de una
 * cache paginada es donde aparecen los desfases.
 */
export function useCambiarEstadoDeComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: string }) =>
      actualizarEstadoRequest(id, estado),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY }),
  });
}
